// 3rd parties
import L from "leaflet";
import "leaflet-rotate"; // Must be imported before extending L.Map
import "leaflet-imageoverlay-rotated";

// Note: This file contains complex Leaflet plugin integrations that require extensive type assertions
// Many of the 'any' types are necessary due to dynamic plugin loading and custom Leaflet extensions

// owner
import { useOrg } from "@/contexts/OrgContext";
import geolayer from "@apis/geolayer";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { hasFeature } from "@/hooks/useFeatures";
import { FeatureKey } from "@/types/generated/FeatureKey";

import { extendedTileLayer } from "./Layers/ExtendedTileLayer";
import { GeozoneLayer } from "./Layers/LayerGeozone";
import { SitemapLayer } from "./Layers/LayerSitemap";
import { layerNetwork } from "./Layers/LayerNetwork";
import { HeatMapLayer } from "./Layers/LayerHeatMap";
import { InfraLayer } from "./Layers/LayerInfra";
import { AssetLayer } from "./Layers/LayerAsset";

// Custom map type definitions (currently unused but kept for future reference)
// import { SensolusMap, GeolayerData } from "@/types/leaflet-custom";

export default function useCustomMap() {
  const { orgId } = useOrg();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (L.Map as any).extend({
    layerControl: null,

    overlayLayers: [],

    baseLayers: [],

    addCustomLayersSucceed: function () {},

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    addCustomLayers: function (id: any, options: any) {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const self = this;
      self.clearCustomOverlayLayers();
      // Check for either geolayer or site map feature
      if (!hasFeature(FeatureKey.loc_geolayer) && !hasFeature(FeatureKey.loc_site_map)) {
        if (self.addCustomLayersSucceed) {
          self.addCustomLayersSucceed();
        }
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      self.getGeolayerData(id, options.isUseOwnerGeolayerData, (data: any) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data?.forEach((layer: any) => {
          if (options && +options.ignoreGeolayerId === layer.id) return;

          try {
            switch (layer.type.name) {
              case "GEOJSON":
                self.addGeoJsonLayer(layer);
                break;
              case "IMAGE":
                self.addImageLayer(layer);
                break;
              case "TILE":
                self.addTileLayer(layer);
                break;
              case "KML":
                self.addKmlLayer(layer);
                break;
              case "ESRI":
                self.addEsriLayer(layer);
                break;
              default:
                break;
            }
          } catch {
            // Intentionally ignore errors during layer processing
          }
        });
        if (self.addCustomLayersSucceed) {
          self.addCustomLayersSucceed();
        }
      });
    },

    getGeolayerData: (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      orgContextOrLoginOrgId: any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      isUseOwnerGeolayerData: any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      callback: any
    ) => {
      if (isUseOwnerGeolayerData) {
        geolayer
          .getByOrg({
            search: "",
            orgId: orgContextOrLoginOrgId ? orgContextOrLoginOrgId : orgId,
          })
          .then(
            ({ data }) => {
              if (callback) {
                callback(data.data);
              }
            },
            (err) => {}
          );
      } else {
        // Use getAllLayersForMapVisibility to get both geolayers (TILE) and site maps (IMAGE)
        void geolayer
          .getAllLayersForMapVisibility({
            orgId: orgContextOrLoginOrgId ? orgContextOrLoginOrgId : orgId,
          })
          .then(({ data }) => {
            if (callback) {
              callback(data.data);
            }
          });
      }
    },

    addInfraLayer: function () {
      const infraLayer = InfraLayer();
      infraLayer.name = "Infra";
      infraLayer.type = "INFRA";
      infraLayer.options = { opacity: 1 };
      this.addOverlayLayer(infraLayer);
    },

    addAssetLayer: function () {
      const infraLayer = AssetLayer();
      infraLayer.name = "asset";
      infraLayer.type = "ASSET";
      infraLayer.options = { opacity: 1 };
      this.addOverlayLayer(infraLayer);
    },

    addGeozoneLayer: function () {
      const geozoneLayer = GeozoneLayer();
      geozoneLayer.name = translate(LanguageKey.geozones_key);
      geozoneLayer.type = "GEOZONE";
      geozoneLayer.options = { opacity: 1 };
      this.addOverlayLayer(geozoneLayer);
    },

    addSitemapPinLayer: function () {
      const sitemapLayer = SitemapLayer();
      sitemapLayer.name = "SitemapPin";
      sitemapLayer.type = "SITEMAP_PIN";
      sitemapLayer.options = { opacity: 1 };
      this.addOverlayLayer(sitemapLayer);
    },

    addNetworkLayer: function () {
      const networkLayer = layerNetwork();
      networkLayer.name = translate(LanguageKey.approximate_locations_key);
      networkLayer.type = "NETWORK";
      this.addOverlayLayer(networkLayer);
    },

    addHeatMapLayer: function () {
      const heatmapLayer = HeatMapLayer({
        // radius should be small ONLY if scaleRadius is true (or small radius is intended)
        // if scaleRadius is false it will be the constant radius used in pixels
        radius: 20,
        maxOpacity: 0.8,
        // scales the radius based on map zoom
        scaleRadius: false,
        // if set to false the heatmap uses the global maximum for colorization
        // if activated: uses the data maximum within the current map boundaries
        //   (there will always be a red spot with useLocalExtremas true)
        useLocalExtrema: true,
        // which field name in your data represents the latitude - default "lat"
        latField: "lat",
        // which field name in your data represents the longitude - default "lng"
        lngField: "lng",
        // which field name in your data represents the data value - default "value"
        valueField: "count",
      });
      heatmapLayer.name = "Heatmap";
      heatmapLayer.type = "HEATMAP";
      this.addOverlayLayer(heatmapLayer);
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    addGeoJsonLayer: function (layer: any) {
      const geojson = L.geoJSON(layer.data);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (geojson as any).name = layer.name;
      this.addOverlayLayer(geojson);
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    addImageLayer: function (layer: any) {
      const image = layer.data;
      const topLeft = L.latLng(image.topLeft.lat, image.topLeft.lng);
      const topRight = L.latLng(image.topRight.lat, image.topRight.lng);
      const bottomLeft = L.latLng(image.bottomLeft.lat, image.bottomLeft.lng);
      const options = image.options || {};
      options.pane = "customPane";
      options.opacity = layer.opacity || 1;
      options.bounds = layer.bounds || 1;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const imageLayer = (L.imageOverlay as any).rotated(
        image.imageSrc,
        topLeft,
        topRight,
        bottomLeft,
        options
      );
      imageLayer.name = layer.name;
      imageLayer.type = "IMAGE";
      imageLayer.category = layer.category;
      imageLayer.id = layer.id;
      imageLayer.description = layer.description;

      this.addOverlayLayer(imageLayer);
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    addTileLayer: function (layer: any) {
      const config = layer.data;
      let maxNativeZoom = 20;
      //maxNativeZoom for sigfox tile
      if (
        layer.data &&
        layer.data.url &&
        layer.data.url.indexOf("sigfox") !== -1
      ) {
        maxNativeZoom = 11;
      }
      const tileLayer = extendedTileLayer(config.url, {
        pane: "customPane",
        minZoom: 2,
        maxNativeZoom: maxNativeZoom,
        maxZoom: 22,
        zoomOffset: config.zoomOffset || 0,
        xOffset: config.xOffset || 0,
        yOffset: config.yOffset || 0,
        opacity: 0.7,
        zIndex: 2,
      });
      tileLayer.type = "TILE";
      tileLayer.name = layer.name;
      tileLayer.category = layer.category;
      tileLayer.description = layer.description;
      tileLayer.id = layer.id;
      this.addOverlayLayer(tileLayer);
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    addKmlLayer: function (layer: any) {
      const kmltext = layer.data.kml;
      const parser = new DOMParser();
      const kml = parser.parseFromString(kmltext, "text/xml");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const kmlLayer = new (L as any).KML(kml);
      kmlLayer.type = "KML";
      kmlLayer.name = layer.name;
      this.addOverlayLayer(kmlLayer);
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    addEsriLayer: function (layer: any) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const esriLayer = (L as any).esri.dynamicMapLayer({
        url: layer.data.url,
        opacity: layer.data.opacity,
      });
      esriLayer.type = "ESRI";
      esriLayer.name = layer.name;
      this.addOverlayLayer(esriLayer);
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getMapOverlayLayerByName: function (layerName: any) {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const self = this;
      for (let i = 0; i < self.overlayLayers.length; i++) {
        if (self.overlayLayers[i].name === layerName) {
          return self.overlayLayers[i];
        }
      }
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getMapBaseLayerByName: function (layerName: any) {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const self = this;
      for (let i = 0; i < self.baseLayers.length; i++) {
        if (self.baseLayers[i].name === layerName) {
          return self.baseLayers[i];
        }
      }
    },

    clearCustomOverlayLayers: function () {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const self = this;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      self.overlayLayers.forEach((layer: any) => {
        if (self.isCustomLayer(layer)) {
          self.removeLayer(layer);
          self.layerControl.removeLayer(layer);
        }
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      self.overlayLayers = self.overlayLayers.filter((layer: any) => {
        return !self.isCustomLayer(layer);
      });
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    isCustomLayer: function (layer: any) {
      return (
        layer.type === "GEOJSON" ||
        layer.type === "IMAGE" ||
        layer.type === "TILE" ||
        layer.type === "KML" ||
        layer.type === "ESRI"
      );
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    addOverlayLayer: function (overlayLayer: any) {
      if (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        !this.overlayLayers.find((layer: any) => layer.name === overlayLayer.name)
      ) {
        this.overlayLayers.push(overlayLayer);
        this.layerControl.addOverlay(overlayLayer, overlayLayer.name);
      }
    },
  });
}
